import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { labApi } from '@/api/lab';
import { Input } from '@/components/ui/input';
import { Search, X } from 'lucide-react';
import { cn } from '@/lib/utils';

interface LabTest {
  id: string;
  name: string;
  code: string;
}

interface LabTestSearchProps {
  onTestSelect: (test: LabTest) => void;
  placeholder?: string;
  className?: string;
}

export function LabTestSearch({ onTestSelect, placeholder = 'Search tests...', className }: LabTestSearchProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const { data: allTests = [] } = useQuery<LabTest[]>({
    queryKey: ['lab-tests'],
    queryFn: async () => {
      const response = await labApi.getLabTests();
      return response.data?.data;
    },
  });

  const filteredTests = allTests.filter(
    (test) =>
      test.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      test.code.toLowerCase().includes(searchQuery.toLowerCase())
  );

  useEffect(() => {
    const onPointerDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, []);

  const handleTestSelect = (test: LabTest) => {
    onTestSelect(test);
    setOpen(false);
    setSearchQuery('');
  };

  return (
    <div ref={rootRef} className={cn('relative', className)}>
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder={placeholder}
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            className="pl-9 pr-9"
          />
          {searchQuery && (
            <button
              type="button"
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
              onClick={() => {
                setSearchQuery('');
                setOpen(false);
              }}
              aria-label="Clear search"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        {open && (
          <button
            type="button"
            className="shrink-0 rounded-md border border-slate-200 px-3 py-2 text-xs font-medium text-slate-600 hover:bg-slate-50"
            onClick={() => setOpen(false)}
          >
            Close
          </button>
        )}
      </div>

      {open && (
        <div className="absolute z-50 mt-1 max-h-60 w-full overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-xl">
          <div className="sticky top-0 border-b border-slate-100 bg-slate-50 px-3 py-2 text-xs font-medium text-slate-500">
            {filteredTests.length} test{filteredTests.length === 1 ? '' : 's'}
            {searchQuery.trim() ? ` matching "${searchQuery.trim()}"` : ' available'}
          </div>
          {filteredTests.length === 0 ? (
            <p className="px-3 py-4 text-sm text-slate-500">No tests found</p>
          ) : (
            filteredTests.map((test) => (
              <button
                key={test.id}
                type="button"
                className="flex w-full flex-col gap-0.5 border-b border-slate-50 px-3 py-2.5 text-left last:border-0 hover:bg-blue-50/80"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handleTestSelect(test)}
              >
                <span className="text-sm font-medium text-slate-900">{test.name}</span>
                <span className="text-xs text-slate-500">{test.code}</span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
